import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import { Router } from 'wouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { RoutesProvider } from '@folklore/routes';
import * as AppPropTypes from '../lib/PropTypes';
import { SiteProvider } from '../contexts/SiteContext';
import { KeysProvider } from '../contexts/KeysContext';
import { ApiProvider } from '../contexts/ApiContext';
import { PagesProvider } from '../contexts/PagesContext';
import { UserProvider } from '../contexts/UserContext';
import { ModalsProvider } from '../contexts/ModalsContext';
import { VideoProvider } from '../contexts/VideoContext';

import App from './App';

const propTypes = {
    site: AppPropTypes.site,
    sites: PropTypes.arrayOf(AppPropTypes.site),
    rootDomain: PropTypes.string,
    intl: PropTypes.shape({
        locale: PropTypes.string,
        locales: PropTypes.arrayOf(PropTypes.string),
        messages: PropTypes.objectOf(PropTypes.string),
    }),
    routes: AppPropTypes.routes.isRequired,
    keys: PropTypes.objectOf(PropTypes.string),
    menus: AppPropTypes.menus,
    pages: AppPropTypes.pages,
    user: AppPropTypes.user,
    contactPageUrl: PropTypes.string,
    socket: PropTypes.shape({
        host: PropTypes.string,
    }),
};

const intlDefaultRichTextElements = {
    br: <br />,
    hr: <hr />,
    nb: (chunks) => (
        <span
            style={{
                whiteSpace: 'nowrap',
            }}
        >
            {chunks}
        </span>
    ),
    p: (chunks) => <p>{chunks}</p>,
    i: (chunks) => <em>{chunks}</em>,
    em: (chunks) => <em>{chunks}</em>,
    pem: (chunks) => (
        <p>
            <em>{chunks}</em>
        </p>
    ),
    b: (chunks) => <strong>{chunks}</strong>,
    strong: (chunks) => <strong>{chunks}</strong>,
    small: (chunks) => <small>{chunks}</small>,
    sup: (chunks) => <sup>{chunks}</sup>,
    sub: (chunks) => <sub>{chunks}</sub>,
    ul: (chunks) => <ul>{chunks}</ul>,
    ol: (chunks) => <ol>{chunks}</ol>,
    li: (chunks) => <li>{chunks}</li>,
    h1: (chunks) => <h1>{chunks}</h1>,
    h2: (chunks) => <h2>{chunks}</h2>,
    h3: (chunks) => <h3>{chunks}</h3>,
    h4: (chunks) => <h4>{chunks}</h4>,
    h5: (chunks) => <h5>{chunks}</h5>,
    h6: (chunks) => <h6>{chunks}</h6>,
};

const defaultProps = {
    site: null,
    sites: [],
    intl: null,
    keys: null,
    rootDomain: null,
    menus: {},
    pages: [],
    user: null,
    contactPageUrl: null,
    socket: {
        host: 'https://localhost',
    },
};

const Container = ({
    site,
    sites,
    intl,
    routes,
    rootDomain,
    menus,
    pages: initialPages,
    user,
    keys,
    // socket,
    contactPageUrl,
}) => {
    const pages = useMemo(
        () =>
            (initialPages || []).reduce(
                (obj, page) => ({
                    ...obj,
                    [page.url]: page,
                }),
                {},
            ),
        [initialPages],
    );
    const { locale, locales, messages } = intl || {};

    const queryClient = useMemo(() => {
        const client = new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: Infinity,
                },
            },
        });
        // if (page !== null) {
        //     const query =
        //         window && !isEmpty(window.location.search)
        //             ? queryString.parse(window.location.search, {
        //                   arrayFormat: 'bracket',
        //               })
        //             : null;
        //     client.setQueryData(['pages', page.url, query], page);
        // }
        return client;
    }, [pages]);

    return (
        <Router hook={null}>
            <IntlProvider
                locale={locale}
                messages={messages || {}}
                defaultRichTextElements={intlDefaultRichTextElements}
            >
                <SiteProvider
                    site={site}
                    sites={sites}
                    rootDomain={rootDomain}
                    menus={menus}
                    locales={locales}
                    contactPageUrl={contactPageUrl}
                >
                    <RoutesProvider routes={routes}>

                        <KeysProvider keys={keys}>
                            <ApiProvider>
                                <QueryClientProvider client={queryClient}>
                                    <PagesProvider pages={pages}>
                                        <UserProvider user={user}>
                                            <ModalsProvider>
                                                <VideoProvider>
                                                    <App />
                                                </VideoProvider>
                                            </ModalsProvider>
                                        </UserProvider>
                                    </PagesProvider>
                                </QueryClientProvider>
                            </ApiProvider>
                        </KeysProvider>
                    </RoutesProvider>
                </SiteProvider>
            </IntlProvider>
        </Router>
    );
};

Container.propTypes = propTypes;
Container.defaultProps = defaultProps;

export default Container;
